import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';

import axios from 'axios';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, { IBrandingSpotApi } from '@services/BrandingsService';
import EventsService from '@services/EventsService';
import { env } from 'env';
import type { IBrandingSpotDriftRow } from '@shared/utils/mapTemplateDrift';

import {
  EditorGroupId,
  EditorNodeKind,
  SpotType,
  getSpotType,
  FOCUS_TO_GROUP_IDS,
} from '../editorCategories';
import { useEditorMode } from '../EditorModeContext';
import { useEditorStyles } from '../styles';
import { useDrift } from '../DriftContext';
import {
  getBrandingEntityCatalogue,
  isBrandingEntityEditorAvailable,
} from '../brandingEntities';
import AiScriptsTreeSection from '../AiScripts/AiScriptsTreeSection';
import { useMapEditor } from '../MapEditorContext';
import { useCanUseMapEditor } from '../useCanUseMapEditor';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import ModuleTree from './ModuleTree';
import ObjectsCsvActions from './ObjectsCsvActions';
import { useSceneTreeStyles } from './styles';

type AnySpot = Record<string, unknown> & {
  ueIdentifier?: string;
  name?: string;
  isActive?: boolean;
  type?: string;
};

const getSpotTypeIcon = (type: SpotType, sx: object) => {
  if (type === 'VIDEO') {
    return <PlayArrowOutlinedIcon sx={sx} />;
  }

  return <ImageOutlinedIcon sx={sx} />;
};

const DEFAULT_LANGUAGE_CODE = 'en';

type SpotMedia = { url?: string; location?: string; previewImage?: string };

// The image a placeholder spot is currently showing — mirrors SpotInspector's
// field precedence. Image spots resolve to their content URL; video spots fall
// back to their preview image; QR spots have no thumbnail.
const getSpotThumbnailUrl = (spot: AnySpot): string | null => {
  const localizations = spot.localizations as
    | Record<string, SpotMedia | undefined>
    | undefined;
  const english = localizations?.[DEFAULT_LANGUAGE_CODE];
  const data = spot.data as SpotMedia | undefined;
  const previewImage =
    english?.previewImage ||
    data?.previewImage ||
    (typeof spot.previewImage === 'string' ? spot.previewImage : undefined);
  const type = getSpotType(spot);

  if (type === 'QR') {
    return null;
  }
  if (type === 'VIDEO') {
    return previewImage || null;
  }

  return (
    english?.url ||
    english?.location ||
    data?.url ||
    data?.location ||
    previewImage ||
    null
  );
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

interface IRailButtonProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
  testId: string;
}

const RailButton = ({
  active,
  icon,
  label,
  tooltip,
  onClick,
  testId,
}: IRailButtonProps) => {
  const treeStyles = useSceneTreeStyles();
  return (
    <Tooltip title={tooltip} placement="right" arrow enterDelay={300}>
      <Box
        sx={[treeStyles.railItem, active && treeStyles.railItemActive]}
        onClick={onClick}
        role="tab"
        aria-label={tooltip}
        aria-selected={active}
        data-testid={testId}
      >
        {icon}
        <Box component="span" sx={treeStyles.railItemLabel}>
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
};

const SceneTree = () => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const treeStyles = useSceneTreeStyles();

  const {
    selectedNode,
    selectMapAndColors,
    selectSpot,
    selectHotspot,
    selectMapLayout,
    selectBrandingEntity,
    activeFocus,
    setActiveFocus,
    isSceneTreeCollapsed,
    toggleSceneTree,
    setAutosaveStatus,
  } = useEditorMode();

  const { rooms: layoutRooms, appendRoom } = useMapEditor();
  const { elements: catalogElements } = useMapEditorCatalog();
  const canUseMapEditor = useCanUseMapEditor();

  // Which branches of the room kit are open. Held here rather than in ModuleTree because that
  // unmounts with the Layout group, and an author's place in the kit should survive collapsing the
  // group to look at something else.
  const [openModuleBranches, setOpenModuleBranches] = useState<
    Record<string, boolean>
  >({});

  const [collapsedGroups, setCollapsedGroups] = useState<
    Partial<Record<EditorGroupId, boolean>>
  >({});

  // Spot thumbnails whose image failed to load — fall back to the type icon.
  const [failedThumbs, setFailedThumbs] = useState<Record<string, boolean>>({});

  const toggleGroup = useCallback((id: EditorGroupId) => {
    setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const {
    roomSpots,
    setRoomSpots,
    brandingSpots,
    setBrandingSpots,
    brandingHotspots,
    setBrandingHotspots,
    currentEvent,
    currentRoom,
    currentBrandingId,
    isBrandingModeEnabled,
    setSelectedCustomizationUeId,
    setSelectedCustomizationTag,
    setSelectedCustomizationSpotType,
    setSelectedCustomizationTargetSize,
    setPendingHotspotPlacement,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { spotDrift } = useDrift();

  const driftByUe = useMemo(() => {
    const map = new Map<string, IBrandingSpotDriftRow<IBrandingSpotApi>>();
    spotDrift.rows.forEach((row) => {
      const ue = normalizeUeIdentifier(row.ueIdentifier);
      if (ue) {
        map.set(ue, row);
      }
    });
    return map;
  }, [spotDrift]);

  const handleSpotSelect = useCallback(
    (spot: AnySpot, ueId: string) => {
      const type = getSpotType(spot);
      setSelectedCustomizationUeId(ueId);
      setSelectedCustomizationTag(ueId);
      setSelectedCustomizationSpotType(type);

      setSelectedCustomizationTargetSize(null);
      selectSpot(ueId);
    },
    [
      selectSpot,
      setSelectedCustomizationUeId,
      setSelectedCustomizationTag,
      setSelectedCustomizationSpotType,
      setSelectedCustomizationTargetSize,
    ],
  );

  const spots = useMemo<AnySpot[]>(() => {
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    return Array.isArray(source) ? (source as AnySpot[]) : [];
  }, [currentBrandingId, isBrandingModeEnabled, brandingSpots, roomSpots]);

  const isMapColorsSelected =
    selectedNode?.kind === EditorNodeKind.MAP_AND_COLORS;

  const isMapLayoutSelected = selectedNode?.kind === EditorNodeKind.MAP_LAYOUT;

  const selectedSpotUeId =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? selectedNode.ueIdentifier
      : null;

  const selectedNodeRef = useRef<HTMLDivElement | null>(null);
  const spotsCollapsed = !!collapsedGroups[EditorGroupId.SPOTS];

  useEffect(() => {
    if (!selectedSpotUeId) {
      return;
    }
    setCollapsedGroups((prev) => {
      if (!prev[EditorGroupId.SPOTS]) {
        return prev;
      }
      return { ...prev, [EditorGroupId.SPOTS]: false };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpotUeId]);

  useEffect(() => {
    if (!selectedSpotUeId || spotsCollapsed) {
      return;
    }
    const node = selectedNodeRef.current;
    if (node) {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedSpotUeId, spotsCollapsed, spots.length]);

  const handleToggleVisibility = useCallback(
    async (e: React.MouseEvent, spot: AnySpot) => {
      e.stopPropagation();
      const nextActive = spot.isActive === false;
      const useBrandingSpots = !!currentBrandingId && isBrandingModeEnabled;

      const brandingSpotId =
        typeof (spot as { brandingSpotId?: unknown }).brandingSpotId ===
        'string'
          ? ((spot as { brandingSpotId: string }).brandingSpotId as string)
          : null;
      const roomSpotId =
        typeof (spot as { roomSpotId?: unknown }).roomSpotId === 'string'
          ? ((spot as { roomSpotId: string }).roomSpotId as string)
          : null;
      const fallbackId =
        typeof (spot as { id?: unknown }).id === 'string'
          ? ((spot as { id: string }).id as string)
          : null;

      setAutosaveStatus('saving');
      try {
        if (useBrandingSpots && currentBrandingId) {
          const id = brandingSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing branding spot id for visibility toggle');
          }
          await BrandingsService.patchBrandingSpot(currentBrandingId, id, {
            isActive: nextActive,
          });
          const refreshed = await BrandingsService.getBrandingSpots(
            currentBrandingId,
            { limit: 200 },
          );
          setBrandingSpots(refreshed.spots ?? []);
        } else if (currentRoom) {
          const id = roomSpotId || fallbackId;
          if (!id) {
            throw new Error('Missing room spot id for visibility toggle');
          }

          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${id}`,
            { isActive: nextActive },
          );
          const refreshed = await EventsService.getRoomSpots(
            currentRoom.roomId,
          );
          setRoomSpots(refreshed);
        }
        sendMessageToUnreal({
          action: 'SPOTS_UPDATED',
          spots: [],
        });
        setAutosaveStatus('saved');
      } catch (toggleError) {
        console.error(
          `Failed to toggle spot visibility: ${
            (toggleError as Error)?.message
          }`,
          {
            ueIdentifier:
              typeof spot.ueIdentifier === 'string' ? spot.ueIdentifier : null,
            brandingId: currentBrandingId,
            roomId: currentRoom?.roomId,
          },
        );
        setAutosaveStatus('error');
      }
    },
    [
      currentBrandingId,
      currentRoom,
      isBrandingModeEnabled,
      sendMessageToUnreal,
      setAutosaveStatus,
      setBrandingSpots,
      setRoomSpots,
    ],
  );

  const hotspots = useMemo<AnySpot[]>(
    () =>
      Array.isArray(brandingHotspots) ? (brandingHotspots as AnySpot[]) : [],
    [brandingHotspots],
  );

  const selectedHotspotId =
    selectedNode?.kind === EditorNodeKind.HOTSPOT
      ? selectedNode.hotspotId
      : null;

  // The catalogue of brandable entities is declared by the build, not by the
  // configuration — it lists what may be overridden, not what is overridden.
  const brandingEntities = useMemo(
    () => getBrandingEntityCatalogue(currentRoom?.map),
    [currentRoom?.map],
  );
  const isObjectsFocusAvailable = isBrandingEntityEditorAvailable(
    currentEvent,
    currentRoom?.map,
  );
  const selectedBrandingEntityId =
    selectedNode?.kind === EditorNodeKind.BRANDING_ENTITY
      ? selectedNode.entityId
      : null;

  /**
   * Creates a 3D-object hotspot draft and opens its inspector.
   *
   * Hotspot authoring is 3D-only (MV-1756): the record is created empty and the
   * user uploads a `.glb` in the inspector, which is what starts the in-world
   * placement. 2D panels are no longer authored here, but existing ones stay
   * editable.
   */
  const handleAddHotspot = useCallback(async () => {
    const brandingHotspotId = crypto.randomUUID();

    if (!currentBrandingId) {
      return;
    }

    const createPayload = {
      isActive: true,
      type: 'OBJECT_3D',
      name: translate('editorMode.newHotspot3D', 'New 3D object'),
      data: { url: '' },
    };

    setAutosaveStatus('saving');
    try {
      let created;
      try {
        created = await BrandingsService.createBrandingHotspot(
          currentBrandingId,
          { brandingHotspotId, ...createPayload },
        );
      } catch (createError) {
        const status = (createError as { response?: { status?: number } })
          ?.response?.status;
        if (status !== 400) {
          throw createError;
        }
        console.warn(
          'Backend rejected client-supplied brandingHotspotId, retrying with a server-generated id',
          { brandingId: currentBrandingId, brandingHotspotId },
        );
        created = await BrandingsService.createBrandingHotspot(
          currentBrandingId,
          createPayload,
        );
      }

      const createdId =
        typeof created?.brandingHotspotId === 'string'
          ? created.brandingHotspotId
          : brandingHotspotId;
      const refreshed = await BrandingsService.getBrandingHotspots(
        currentBrandingId,
        { limit: 200 },
      );
      setBrandingHotspots(refreshed.hotspots ?? []);
      setPendingHotspotPlacement({
        brandingHotspotId: createdId,
        isCreated: true,
      });
      setAutosaveStatus('saved');
      selectHotspot(createdId);
    } catch (createError) {
      console.error(
        `Failed to create 3D hotspot draft: ${(createError as Error)?.message}`,
        { brandingId: currentBrandingId, brandingHotspotId },
      );
      setAutosaveStatus('error');
    }
  }, [
    currentBrandingId,
    setPendingHotspotPlacement,
    setBrandingHotspots,
    setAutosaveStatus,
    selectHotspot,
    translate,
  ]);

  const handleToggleHotspotVisibility = useCallback(
    async (e: React.MouseEvent, hotspot: AnySpot) => {
      e.stopPropagation();
      if (!currentBrandingId) {
        return;
      }
      const id =
        typeof (hotspot as { brandingHotspotId?: unknown })
          .brandingHotspotId === 'string'
          ? ((hotspot as { brandingHotspotId: string })
              .brandingHotspotId as string)
          : null;
      if (!id) {
        return;
      }
      const nextActive = hotspot.isActive === false;
      setAutosaveStatus('saving');
      try {
        await BrandingsService.patchBrandingHotspot(currentBrandingId, id, {
          isActive: nextActive,
        });
        const refreshed = await BrandingsService.getBrandingHotspots(
          currentBrandingId,
          { limit: 200 },
        );
        setBrandingHotspots(refreshed.hotspots ?? []);
        sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
        setAutosaveStatus('saved');
      } catch (toggleError) {
        console.error(
          `Failed to toggle hotspot visibility: ${
            (toggleError as Error)?.message
          }`,
          { brandingId: currentBrandingId, brandingHotspotId: id },
        );
        setAutosaveStatus('error');
      }
    },
    [
      currentBrandingId,
      sendMessageToUnreal,
      setAutosaveStatus,
      setBrandingHotspots,
    ],
  );

  if (isSceneTreeCollapsed) {
    return (
      <Box
        sx={[styles.glassPanel, styles.sceneTreeCollapsedPill]}
        onClick={toggleSceneTree}
        data-testid="scene-tree-expand-pill"
      >
        <ViewSidebarOutlinedIcon fontSize="small" />
        <Typography sx={{ fontSize: '13px', color: 'inherit' }}>
          {translate('editorMode.scene')}
        </Typography>
      </Box>
    );
  }

  const visibleGroupIds = FOCUS_TO_GROUP_IDS[activeFocus];

  const renderEnvironment = () => (
    <Box key={EditorGroupId.ENVIRONMENT} sx={treeStyles.groupSection}>
      <Box sx={treeStyles.groupLabelRow}>
        <Typography sx={treeStyles.groupLabel}>
          {translate('editorMode.groupEnvironment')}
        </Typography>
      </Box>
      <Box
        sx={[treeStyles.node, isMapColorsSelected && treeStyles.nodeSelected]}
        onClick={selectMapAndColors}
        data-testid="scene-tree-map-colors-node"
      >
        <PaletteOutlinedIcon sx={treeStyles.nodeIcon} />
        <Typography sx={treeStyles.nodeLabel}>
          {translate('editorMode.mapAndColors')}
        </Typography>
      </Box>
      <Typography sx={treeStyles.driftNote}>
        {translate('editorMode.driftEnvironmentNote')}
      </Typography>
    </Box>
  );

  const renderCoverageMarker = (ueId: string) => {
    const row = driftByUe.get(normalizeUeIdentifier(ueId));
    if (!row) {
      return null;
    }
    if (row.status === 'configured') {
      return (
        <CheckCircleOutlineOutlinedIcon
          sx={[treeStyles.coverageMarker, treeStyles.coverageConfigured]}
        />
      );
    }
    if (row.status === 'placeholder') {
      return (
        <RadioButtonUncheckedOutlinedIcon
          sx={[treeStyles.coverageMarker, treeStyles.coveragePlaceholder]}
        />
      );
    }
    return null;
  };

  const renderGhostSpotNode = (
    row: IBrandingSpotDriftRow<IBrandingSpotApi>,
    variant: 'missing' | 'orphaned',
  ) => {
    const ueId = normalizeUeIdentifier(row.ueIdentifier);
    const sourceSpot =
      variant === 'orphaned' && row.brandingSpot
        ? (row.brandingSpot as AnySpot)
        : ({ ueIdentifier: ueId, type: row.type } as AnySpot);
    const type = getSpotType(sourceSpot);
    const fallbackName =
      variant === 'orphaned' && typeof row.brandingSpot?.name === 'string'
        ? row.brandingSpot.name
        : '';
    const name = fallbackName || ueId;
    const sub =
      variant === 'missing'
        ? row.regionName || translate('editorMode.driftInBuild')
        : translate('editorMode.driftRemoved');
    const isSelected =
      !!selectedSpotUeId && ueId === normalizeUeIdentifier(selectedSpotUeId);

    return (
      <Box
        key={`${variant}:${ueId}`}
        ref={isSelected ? selectedNodeRef : undefined}
        sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
        onClick={() => ueId && handleSpotSelect(sourceSpot, ueId)}
        data-testid={
          variant === 'missing'
            ? `scene-tree-missing-row-${ueId}`
            : `scene-tree-orphaned-row-${ueId}`
        }
      >
        {getSpotTypeIcon(type, treeStyles.nodeIcon)}
        <Box sx={treeStyles.ghostNodeLabel}>
          <Box sx={treeStyles.ghostNodeTitleRow}>
            <Typography sx={treeStyles.ghostNodeTitle}>{name}</Typography>
            {variant === 'missing' && row.isNew ? (
              <Box component="span" sx={treeStyles.newBadge}>
                {translate('editorMode.driftNew')}
              </Box>
            ) : null}
          </Box>
          <Typography sx={treeStyles.ghostNodeSub}>{sub}</Typography>
        </Box>
        {variant === 'missing' ? (
          <AddCircleOutlineOutlinedIcon sx={treeStyles.ghostMarkerMissing} />
        ) : (
          <ReportProblemOutlinedIcon sx={treeStyles.ghostMarkerOrphaned} />
        )}
      </Box>
    );
  };

  const renderSpots = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.SPOTS];

    const orphanRows = spotDrift.rows.filter(
      (row) => row.status === 'orphaned',
    );
    const missingRows = spotDrift.rows.filter(
      (row) => row.status === 'missing',
    );
    const orphanUeSet = new Set(
      orphanRows.map((row) => normalizeUeIdentifier(row.ueIdentifier)),
    );
    const inConfigSpots = spots.filter(
      (spot) => !orphanUeSet.has(normalizeUeIdentifier(spot.ueIdentifier)),
    );

    return (
      <React.Fragment key={EditorGroupId.SPOTS}>
        <Box sx={treeStyles.groupSection}>
          <Box
            sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
            onClick={() => toggleGroup(EditorGroupId.SPOTS)}
            data-testid="scene-tree-spots-group-header"
          >
            <ExpandMoreOutlinedIcon
              sx={[
                treeStyles.groupChevron,
                isCollapsed && treeStyles.groupChevronCollapsed,
              ]}
            />
            <Typography sx={treeStyles.groupLabel}>
              {translate('editorMode.groupSpots')}
            </Typography>
            {inConfigSpots.length > 0 && (
              <Typography sx={treeStyles.groupCount}>
                {inConfigSpots.length}
              </Typography>
            )}
          </Box>

          {!isCollapsed && inConfigSpots.length === 0 && (
            <Typography sx={treeStyles.emptyText}>
              {translate('editorMode.noSpots')}
            </Typography>
          )}

          {!isCollapsed &&
            inConfigSpots.map((spot, idx) => {
              const ueId = normalizeUeIdentifier(spot.ueIdentifier);
              const key = ueId || `spot-${idx}`;
              const type = getSpotType(spot);
              const isSelected =
                !!selectedSpotUeId &&
                ueId === normalizeUeIdentifier(selectedSpotUeId);
              const isHidden = spot.isActive === false;
              const name =
                typeof spot.name === 'string' && spot.name.length > 0
                  ? spot.name
                  : ueId || translate('adminToolbox.unnamed');
              const rawThumbUrl = getSpotThumbnailUrl(spot);
              const thumbUrl = failedThumbs[key] ? null : rawThumbUrl;

              return (
                <Tooltip
                  key={key}
                  title={name}
                  placement="right"
                  enterDelay={350}
                  arrow
                >
                  <Box
                    ref={isSelected ? selectedNodeRef : undefined}
                    sx={[
                      treeStyles.node,
                      isSelected && treeStyles.nodeSelected,
                    ]}
                    onClick={() => ueId && handleSpotSelect(spot, ueId)}
                    data-testid={`scene-tree-spot-row-${ueId}`}
                  >
                    {thumbUrl ? (
                      <Box
                        component="img"
                        src={thumbUrl}
                        alt=""
                        sx={treeStyles.nodeThumb}
                        onError={() => {
                          setFailedThumbs((prev) => ({ ...prev, [key]: true }));
                        }}
                      />
                    ) : (
                      getSpotTypeIcon(type, treeStyles.nodeIcon)
                    )}
                    <Typography sx={treeStyles.nodeLabel}>{name}</Typography>
                    {renderCoverageMarker(ueId)}
                    <Box sx={treeStyles.nodeActions}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleVisibility(e, spot)}
                        sx={[
                          treeStyles.nodeEye,
                          isHidden && treeStyles.nodeEyeHidden,
                        ]}
                        aria-label={translate(
                          'editorMode.toggleVisibility',
                          'Toggle visibility',
                        )}
                        data-testid={`scene-tree-spot-row-${ueId}-visibility-btn`}
                      >
                        {isHidden ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
        </Box>

        {missingRows.length > 0 && (
          <Box sx={treeStyles.groupSection}>
            <Box
              sx={treeStyles.groupLabelRow}
              data-testid="scene-tree-missing-group-header"
            >
              <Typography
                sx={[treeStyles.groupLabel, treeStyles.driftGroupLabelAmber]}
              >
                {translate('editorMode.driftMissingGroup')}
              </Typography>
              <Typography sx={treeStyles.groupCount}>
                {missingRows.length}
              </Typography>
            </Box>
            {missingRows.map((row) => renderGhostSpotNode(row, 'missing'))}
          </Box>
        )}

        {orphanRows.length > 0 && (
          <Box sx={treeStyles.groupSection}>
            <Box
              sx={treeStyles.groupLabelRow}
              data-testid="scene-tree-orphaned-group-header"
            >
              <Typography
                sx={[treeStyles.groupLabel, treeStyles.driftGroupLabelRed]}
              >
                {translate('editorMode.driftOrphanedGroup')}
              </Typography>
              <Typography sx={treeStyles.groupCount}>
                {orphanRows.length}
              </Typography>
            </Box>
            {orphanRows.map((row) => renderGhostSpotNode(row, 'orphaned'))}
          </Box>
        )}
      </React.Fragment>
    );
  };

  const renderHotspots = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.HOTSPOTS];

    return (
      <Box key={EditorGroupId.HOTSPOTS} sx={treeStyles.groupSection}>
        <Box
          sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
          onClick={() => toggleGroup(EditorGroupId.HOTSPOTS)}
        >
          <ExpandMoreOutlinedIcon
            sx={[
              treeStyles.groupChevron,
              isCollapsed && treeStyles.groupChevronCollapsed,
            ]}
          />
          <Typography sx={treeStyles.groupLabel}>
            {translate('editorMode.groupHotspots', 'Hotspots')}
          </Typography>
          {hotspots.length > 0 && (
            <Typography sx={treeStyles.groupCount}>
              {hotspots.length}
            </Typography>
          )}
        </Box>

        {!isCollapsed && (
          <Box
            component="button"
            type="button"
            onClick={handleAddHotspot}
            data-testid="scene-tree-add-hotspot-btn"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              padding: '9px 12px',
              margin: '2px 0 6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              color: 'inherit',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <AddCircleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
            {translate('editorMode.addHotspot', 'Add hotspot')}
          </Box>
        )}

        {!isCollapsed && hotspots.length === 0 && (
          <Typography sx={treeStyles.emptyText}>
            {translate(
              'editorMode.noHotspots',
              'No hotspots in this configuration',
            )}
          </Typography>
        )}

        {!isCollapsed &&
          hotspots.map((hotspot, idx) => {
            const id =
              typeof (hotspot as { brandingHotspotId?: unknown })
                .brandingHotspotId === 'string'
                ? ((hotspot as { brandingHotspotId: string })
                    .brandingHotspotId as string)
                : '';
            const key = id || `hotspot-${idx}`;
            const is3D =
              String(hotspot.type ?? '').toUpperCase() === 'OBJECT_3D';
            const isSelected = !!selectedHotspotId && id === selectedHotspotId;
            const isHidden = hotspot.isActive === false;
            const name =
              typeof hotspot.name === 'string' && hotspot.name.length > 0
                ? hotspot.name
                : id || translate('adminToolbox.unnamed', 'Unnamed');

            return (
              <Tooltip
                key={key}
                title={name}
                placement="right"
                enterDelay={350}
                arrow
              >
                <Box
                  sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
                  onClick={() => id && selectHotspot(id)}
                >
                  {is3D ? (
                    <ExtensionOutlinedIcon sx={treeStyles.nodeIcon} />
                  ) : (
                    <ImageOutlinedIcon sx={treeStyles.nodeIcon} />
                  )}
                  <Typography sx={treeStyles.nodeLabel}>{name}</Typography>
                  <Box sx={treeStyles.nodeActions}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleToggleHotspotVisibility(e, hotspot)}
                      sx={[
                        treeStyles.nodeEye,
                        isHidden && treeStyles.nodeEyeHidden,
                      ]}
                      aria-label={translate(
                        'editorMode.toggleVisibility',
                        'Toggle visibility',
                      )}
                    >
                      {isHidden ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
      </Box>
    );
  };

  // The catalog is the palette, not the layout: picking a module appends it to the chain the
  // inspector owns, so the same module can be added repeatedly and nothing here carries a
  // selected state.
  //
  // Picking happens in a popup rather than as a flat list of rows, because the shipped kits
  // name their modules by geometry ("Corridor 1a Short Left") — a list of those reads as a
  // wall of near-identical strings, while what an author actually decides is three questions:
  // what kind, which way, how big. The popup asks those, and skips any it can answer itself.
  const renderMapEditor = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.MAP_EDITOR];

    return (
      <Box key={EditorGroupId.MAP_EDITOR} sx={treeStyles.groupSection}>
        <Box
          sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
          onClick={() => toggleGroup(EditorGroupId.MAP_EDITOR)}
        >
          <ExpandMoreOutlinedIcon
            sx={[
              treeStyles.groupChevron,
              isCollapsed && treeStyles.groupChevronCollapsed,
            ]}
          />
          <Typography sx={treeStyles.groupLabel}>
            {translate('editorMode.groupMapEditor', 'Map editor')}
          </Typography>
          {layoutRooms.length > 0 && (
            <Typography sx={treeStyles.groupCount}>
              {layoutRooms.length}
            </Typography>
          )}
        </Box>

        {/* The way back to the panel after it is closed. Without it the only thing that
            reopened the inspector was adding a room, so an author who closed it had to
            change their layout to see it again — and closing it is the obvious move when
            you want an unobstructed look at the floor plan you just built.

            A node row rather than a header button, matching "Map and colours" in the
            environment group: the group header is the collapse toggle everywhere in this
            tree, and overloading it here would break that. */}
        {!isCollapsed && (
          <Box
            sx={[
              treeStyles.node,
              isMapLayoutSelected && treeStyles.nodeSelected,
            ]}
            onClick={selectMapLayout}
            data-testid="scene-tree-map-layout-node"
          >
            <ViewQuiltOutlinedIcon sx={treeStyles.nodeIcon} />
            <Typography sx={treeStyles.nodeLabel}>
              {translate('editorMode.mapEditorOpenPanel', 'Layout')}
            </Typography>
            {layoutRooms.length > 0 && (
              <Box sx={treeStyles.nodeActions}>
                <Typography sx={treeStyles.groupCount}>
                  {layoutRooms.length}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Adding also opens the Layout panel: the room lands at the end of a list the author
            cannot see from here, and a click with no visible effect reads as a failure. */}
        {!isCollapsed && (
          <ModuleTree
            elements={catalogElements}
            openKeys={openModuleBranches}
            onToggleKey={(key) =>
              setOpenModuleBranches((current) => ({
                ...current,
                [key]: !current[key],
              }))
            }
            onPick={(elementId) => {
              appendRoom(elementId);
              selectMapLayout();
            }}
          />
        )}

        {/* Only when the panel is closed — otherwise it is a button that does nothing to a
            panel already in front of you. Closing the inspector is how an author clears the
            view to look at the floor plan. */}
        {!isCollapsed && !isMapLayoutSelected && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewQuiltOutlinedIcon />}
            sx={treeStyles.addButton}
            onClick={selectMapLayout}
            data-testid="scene-tree-open-map-editor-btn"
          >
            {translate(
              'editorMode.mapEditorOpenPanelButton',
              'Open map editor',
            )}
          </Button>
        )}
      </Box>
    );
  };

  const renderObjects = () => {
    const isCollapsed = !!collapsedGroups[EditorGroupId.OBJECTS];

    return (
      <Box key={EditorGroupId.OBJECTS} sx={treeStyles.groupSection}>
        <Box
          sx={[treeStyles.groupLabelRow, treeStyles.groupLabelRowCollapsible]}
          onClick={() => toggleGroup(EditorGroupId.OBJECTS)}
          data-testid="scene-tree-objects-group-header"
        >
          <ExpandMoreOutlinedIcon
            sx={[
              treeStyles.groupChevron,
              isCollapsed && treeStyles.groupChevronCollapsed,
            ]}
          />
          <Typography sx={treeStyles.groupLabel}>
            {translate('editorMode.groupObjects', 'Objects')}
          </Typography>
          <Typography sx={treeStyles.groupCount}>
            {brandingEntities.length}
          </Typography>
          <ObjectsCsvActions entities={brandingEntities} />
        </Box>

        {!isCollapsed &&
          brandingEntities.map((entity) => {
            const isSelected = entity.entityId === selectedBrandingEntityId;
            const name = entity.displayName || entity.entityId;
            // One entity can live on several maps and shares a single value
            // set, so the maps it appears on are part of the row.
            const maps = entity.maps.join(' · ');

            return (
              <Tooltip
                key={entity.entityId}
                title={name}
                placement="right"
                enterDelay={350}
                arrow
              >
                <Box
                  sx={[treeStyles.node, isSelected && treeStyles.nodeSelected]}
                  onClick={() => selectBrandingEntity(entity.entityId)}
                  data-testid={`scene-tree-object-row-${entity.entityId}`}
                >
                  <CategoryOutlinedIcon sx={treeStyles.nodeIcon} />
                  <Box sx={treeStyles.ghostNodeLabel}>
                    <Typography sx={treeStyles.ghostNodeTitle}>
                      {name}
                    </Typography>
                    <Typography sx={treeStyles.ghostNodeSub}>{maps}</Typography>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
      </Box>
    );
  };

  return (
    <Box sx={[styles.glassPanel, styles.sceneTreeWrap, { overflow: 'hidden' }]}>
      <Box sx={styles.panelHeader}>
        <Box sx={styles.panelTitleRow}>
          <LayersOutlinedIcon sx={styles.panelTitleIcon} />
          <Typography sx={styles.panelTitle}>
            {translate('editorMode.scene')}
          </Typography>
        </Box>
        <IconButton
          onClick={toggleSceneTree}
          sx={styles.panelIconButton}
          size="small"
          aria-label={translate('editorMode.collapseTree', 'Collapse scene')}
          data-testid="scene-tree-collapse-btn"
        >
          <ViewSidebarOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={treeStyles.railLayout}>
        <Box sx={treeStyles.rail} role="tablist">
          <RailButton
            active={activeFocus === 'environment'}
            icon={<PublicOutlinedIcon />}
            label={translate('editorMode.railMap')}
            tooltip={translate('editorMode.focusEnvironment')}
            onClick={() => setActiveFocus('environment')}
            testId="scene-tree-focus-environment-tab"
          />
          <RailButton
            active={activeFocus === 'spots'}
            icon={<ImageOutlinedIcon />}
            label={translate('editorMode.railSpots')}
            tooltip={translate('editorMode.focusSpots')}
            onClick={() => setActiveFocus('spots')}
            testId="scene-tree-focus-spots-tab"
          />
          <RailButton
            active={activeFocus === 'hotspots'}
            icon={<PlaceOutlinedIcon />}
            label={translate('editorMode.railHotspots', 'Hotspots')}
            tooltip={translate('editorMode.focusHotspots', 'Hotspots')}
            onClick={() => setActiveFocus('hotspots')}
            testId="scene-tree-focus-hotspots-tab"
          />
          <RailButton
            active={activeFocus === 'assistant'}
            icon={<SmartToyOutlinedIcon />}
            label={translate('editorMode.railScripts')}
            tooltip={translate('editorMode.focusAssistant')}
            onClick={() => setActiveFocus('assistant')}
            testId="scene-tree-focus-assistant-tab"
          />
          {/* Absent, not disabled: a map whose build ships no room kit has nothing to
              offer and nothing to explain, so the rail simply does not have this tab. */}
          {canUseMapEditor ? (
            <RailButton
              active={activeFocus === 'mapEditor'}
              icon={<ViewQuiltOutlinedIcon />}
              label={translate('editorMode.railMapEditor', 'Layout')}
              tooltip={translate('editorMode.focusMapEditor', 'Map editor')}
              onClick={() => setActiveFocus('mapEditor')}
              testId="scene-tree-focus-map-editor-tab"
            />
          ) : null}
          {isObjectsFocusAvailable && (
            <RailButton
              active={activeFocus === 'objects'}
              icon={<CategoryOutlinedIcon />}
              label={translate('editorMode.railObjects', 'Objects')}
              tooltip={translate('editorMode.focusObjects', 'Objects')}
              onClick={() => setActiveFocus('objects')}
              testId="scene-tree-focus-objects-tab"
            />
          )}
        </Box>

        <Box sx={treeStyles.railBody}>
          {visibleGroupIds.includes(EditorGroupId.ENVIRONMENT) &&
            renderEnvironment()}
          {visibleGroupIds.includes(EditorGroupId.SPOTS) && renderSpots()}
          {visibleGroupIds.includes(EditorGroupId.HOTSPOTS) && renderHotspots()}
          {visibleGroupIds.includes(EditorGroupId.AI_SCRIPTS) && (
            <AiScriptsTreeSection />
          )}
          {canUseMapEditor &&
            visibleGroupIds.includes(EditorGroupId.MAP_EDITOR) &&
            renderMapEditor()}
          {visibleGroupIds.includes(EditorGroupId.OBJECTS) &&
            isObjectsFocusAvailable &&
            renderObjects()}
        </Box>
      </Box>
    </Box>
  );
};

export default SceneTree;
